/*
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2020
 *
 *==================================================
 */
//Standard libraries
import React from "react";
import { useTranslation } from "react-i18next";
import Grid from "@material-ui/core/Grid";
import {
  Typography,
  Switch,
  Divider,
  FormControlLabel,
} from "@material-ui/core";

//custom library
import { Alert, Button, Paper } from "../components";
import EditCustomerInformation from "./edit-customer-information";
import { useShopOnBehalf } from "../hooks/use-shop-on-behalf";

export default function CustomerInformation({
  cusinfo,
  setCusinfo,
  showEditForm,
  setShoweditForm,
  handleChange,
  clickSubmit,
  changeCustomerStatus,
  showCustomerControl,
  displayAlert,
  canSave,
  editFormData,
  initializeEditForm,
}: any) {
  const isV9 = process.env.COMMERCE_VERSION === "9";
  const { t } = useTranslation();
  const { shopOnBehalf } = useShopOnBehalf();

  const handleEditClick = () => {
    initializeEditForm(cusinfo);
    setShoweditForm(true);
  };
  const msg = t("Customer.UpdateSuccessAlert");
  return showEditForm ? (
    <EditCustomerInformation
      {...{
        cusinfo,
        setShoweditForm,
        setCusinfo,
        handleChange,
        clickSubmit,
        canSave,
        editFormData,
      }}
    />
  ) : (
    <>
      <Paper>
        <Grid container direction="column" spacing={2}>
          <Grid item container justify="space-between" alignItems="center">
            <Grid item>
              <Typography variant="h2">
                {t("Customer.CustomerDetailTitle")}
              </Typography>
            </Grid>
            {showCustomerControl && (
              <Grid item>
                <Button
                  color="secondary"
                  size="small"
                  disabled={
                    isV9
                      ? cusinfo.status === 0
                      : cusinfo.userRegistry?.status === "0"
                  }
                  onClick={() => {
                    shopOnBehalf(cusinfo);
                  }}>
                  {t("Table.Actions.AccessCustomerAccount")}
                </Button>
              </Grid>
            )}
          </Grid>
          {showCustomerControl && (
            <>
              <Grid item>
                <Divider />
              </Grid>
              <Grid
                item
                container
                direction="row"
                justify="flex-start"
                alignItems="center"
                spacing={2}>
                <Grid item>
                  <Typography variant="h3">
                    {t("Customer.CustomerAccountStatus")}
                  </Typography>
                </Grid>
                <Grid item>
                  <FormControlLabel
                    disabled={
                      isV9
                        ? cusinfo?.status === undefined
                        : cusinfo?.userRegistry?.status === undefined
                    }
                    control={
                      <Switch
                        checked={
                          isV9
                            ? cusinfo?.status === 1
                            : cusinfo?.userRegistry?.status === 1
                        }
                        onChange={() => {
                          changeCustomerStatus(cusinfo);
                        }}
                        name="changeCustomerStatus"
                        color="primary"
                      />
                    }
                    label={t(
                      `Customer.Status${
                        isV9 ? cusinfo?.status : cusinfo?.userRegistry?.status
                      }`
                    )}
                  />
                </Grid>
              </Grid>
            </>
          )}
          <Grid item>
            <Button color="secondary" size="small" onClick={handleEditClick}>
              {t("Customer.EditCustomerDetails")}
            </Button>
          </Grid>
          <Grid item>
            <Typography variant="body2">{t("Customer.LoginID")}</Typography>
            <Typography variant="body1">
              {/* {cusinfo?.customerDetails?.logonId} */}
              {cusinfo?.logonId}
            </Typography>
          </Grid>
          <Grid item container spacing={2} direction="row">
            <Grid item xs={6} sm={5} md={4} lg={3} xl={2}>
              <Typography variant="body2">{t("Customer.FirstName")}</Typography>
              <Typography variant="body1">
                {isV9
                  ? cusinfo?.address?.firstName
                  : cusinfo?.address?.firstName}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={5} md={4} lg={3} xl={2}>
              <Typography variant="body2">{t("Customer.LastName")}</Typography>
              <Typography variant="body1">
                {isV9 ? cusinfo?.address?.lastName : cusinfo?.address?.lastName}
              </Typography>
            </Grid>
          </Grid>
          <Grid item>
            <Typography variant="h3">{t("Customer.ContactDetails")}</Typography>
          </Grid>
          <Grid item>
            <Typography variant="body2">
              {t("Customer.EmailAddress")}
            </Typography>
            <Typography variant="body1">
              {isV9 ? cusinfo?.address?.email1 : cusinfo?.address?.email1}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="body2">{t("Customer.PhoneNumber")}</Typography>
            <Typography variant="body1">
              {isV9 ? cusinfo?.address?.phone1 : cusinfo?.address?.phone1}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="h3">{t("Customer.AddressDetails")}</Typography>
          </Grid>
          <Grid item container spacing={2} direction="row">
            <Grid item xs={6} sm={5} md={4} lg={3} xl={2}>
              <Typography variant="body2">
                {t("Customer.StreetAddress")}
              </Typography>
              <Typography variant="body1">
                {isV9 ? cusinfo?.address?.address1 : cusinfo?.address?.address1}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={5} md={4} lg={3} xl={2}>
              <Typography variant="body2">{t("Customer.ZipCode")}</Typography>
              <Typography variant="body1">
                {cusinfo?.address?.zipCode}
              </Typography>
            </Grid>
          </Grid>
          <Grid item container spacing={2} direction="row">
            <Grid item xs={6} sm={5} md={4} lg={3} xl={2}>
              <Typography variant="body2">{t("Customer.State")}</Typography>
              <Typography variant="body1">
                {isV9 ? cusinfo?.address?.state : cusinfo?.address?.state}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={5} md={4} lg={3} xl={2}>
              <Typography variant="body2">{t("Customer.Country")}</Typography>
              <Typography variant="body1">
                {isV9 ? cusinfo?.address?.country : cusinfo?.address?.country}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
      <Grid
        container
        item
        direction="column"
        justify="flex-end"
        alignItems="center"
        className="alert-msg-margin">
        {displayAlert && (
          <Alert
            {...{
              msg,
            }}
          />
        )}
      </Grid>
    </>
  );
}
